import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2, Quote, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoryDetail() {
  const { id } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/stories/${id}`)
      .then((r) => setStory(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="story-loading">
        <Loader2 className="w-8 h-8 text-teal animate-spin" />
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white" data-testid="story-not-found">
        <div className="text-center max-w-md px-6">
          <h1 className="font-display text-3xl text-navy mb-4">Story not found</h1>
          <Link to="/stories" className="btn-primary">
            <ArrowLeft className="w-4 h-4" /> All stories
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="story-detail-page" className="bg-white">
      <article>
        <section className="py-16 lg:py-20 bg-gradient-hero" data-testid="story-hero">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <Link
              to="/stories"
              className="inline-flex items-center gap-2 text-teal font-bold text-sm mb-8 hover:gap-3 transition-all"
              data-testid="back-to-stories"
            >
              <ArrowLeft className="w-4 h-4" /> All stories
            </Link>
            <p className="eyebrow mb-4">{story.category}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
              {story.title}
            </h1>
            <div className="mt-7 flex flex-wrap items-center gap-4 text-sm text-muted-ink">
              <span className="font-semibold text-navy">{story.by}</span>
              <span className="w-1 h-1 rounded-full bg-muted-ink" />
              <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {story.date}</span>
            </div>
          </div>
        </section>

        <section className="py-12 lg:py-16" data-testid="story-body">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <div className="relative pl-12 sm:pl-16 mb-10 pb-10 border-b border-slate-200">
              <Quote className="absolute left-0 top-0 w-8 h-8 text-teal" fill="currentColor" />
              <p className="text-2xl sm:text-3xl text-navy font-display leading-[1.25]">
                {story.summary}
              </p>
            </div>

            <div className="space-y-6 text-[17px] text-muted-ink leading-[1.8]">
              {story.body?.map((p, i) => (
                <p key={i} data-testid={`story-paragraph-${i}`}>{p}</p>
              ))}
            </div>

            <div className="mt-12 p-6 rounded-2xl bg-cream border border-slate-200 text-sm text-muted-ink leading-relaxed">
              <p>
                <span className="font-bold text-navy">A note on names.</span> Stories on this page are real, shared with permission, and lightly edited for clarity and privacy. Names and identifying details may be changed at the storyteller's request.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-navy text-white" data-testid="story-cta">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
            <h3 className="font-display text-3xl sm:text-4xl text-white mb-5 leading-[1.1]">
              Have a story to <span className="hl-teal">share?</span>
            </h3>
            <p className="text-white/80 mb-8">
              Your words might be the moment someone else realizes they're not alone.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact" className="btn-primary" data-testid="story-share-cta">
                Share your story <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/stories" className="btn-outline-teal !border-white !text-white hover:!bg-white hover:!text-navy" data-testid="story-more-cta">
                Read more stories
              </Link>
            </div>
          </div>
        </section>
      </article>
    </div>
  );
}
